import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Skeleton,
  TablePagination,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../hooks/useAuth';
import { getOverviewData, getRecentActivities } from '../store/slices/dashboardSlice';
import { extractList } from '../utils/response';
import { toast } from 'react-toastify';
import PageLayout from '../components/layout/PageLayout';

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const overview = useSelector((s) => s.dashboard.overview);
  const recentActivitiesRes = useSelector((s) => s.dashboard.recentActivities);
  const loading = useSelector((s) => s.dashboard.loading);

  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 5,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const dashboardData = {
    overview: overview?.data ?? overview,
    recentActivities: {
      list: extractList(recentActivitiesRes),
      pagination: recentActivitiesRes?.data?.pagination ?? recentActivitiesRes?.pagination ?? {
        total: 0,
        current: 1,
        last: 1
      }
    },
  };

  const loadDashboardData = () => {
    dispatch(getOverviewData());
  };

  const loadRecentActivities = async (page = pagination.page, pageSize = pagination.pageSize, search = searchTerm) => {
    try {
      setActivitiesLoading(true);
      const res = await dispatch(getRecentActivities({ page, pageSize, search })).unwrap();
      if (res?.success && res?.data?.pagination) {
        setPagination(prev => ({ ...prev, total: res.data.pagination?.total || 0 }));
      }
    } catch (error) {
      toast.error('Failed to load recent activities.');
    } finally {
      setActivitiesLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle Enter key press in search field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (user?.roles?.[0]?.name === 'Admin') {
      loadRecentActivities();
    }
  }, [pagination.page, pagination.pageSize, searchTerm, user]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newSize) => {
    setPagination({ page: 1, pageSize: newSize, total: pagination.total });
  };

  const StatCard = ({ title, value, icon, color = 'primary', subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              bgcolor: `${color}.main`,
              color: 'white',
              borderRadius: 1,
              p: 1,
              mr: 2
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          {loading ? <Skeleton width={60} /> : value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {loading ? <Skeleton width={100} /> : subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Extract data for StatCards
  const totalDbaUnderwriting = dashboardData.overview?.status?.find(s => s?.name === 'under-writing')?.dbas_count || 0;
  const totalDbaPending = dashboardData.overview?.status?.find(s => s?.name === 'pending')?.dbas_count || 0;
  const totalDbaComplete = dashboardData.overview?.status?.find(s => s?.name === 'complete')?.dbas_count || 0;
  const totalUsers = dashboardData?.overview?.total_users;

  const userRole = user?.roles?.[0]?.name;
  const userName = user?.name;

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'grey.800', mb: 1 }}>
              {loading ? <Skeleton width={300} /> : `Welcome back, ${userName || "User"} !`}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {loading ? <Skeleton width={400} /> : "Here's what's happening with your merchants today."}
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Dba Underwriting"
                value={totalDbaUnderwriting}
                icon={<PeopleIcon />}
                color="primary"
                subtitle="Active merchants"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Dba Pending"
                value={totalDbaPending}
                icon={<TrendingUpIcon />}
                color="success"
                subtitle="Merchants added"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Dba Complete"
                value={totalDbaComplete}
                icon={<MoneyIcon />}
                color="warning"
                subtitle="This month"
              />
            </Grid>
            {(userRole === 'Admin' || userRole === 'Team Lead') && (
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Users"
                  value={userRole === 'Team Lead' ? totalUsers - 1 : totalUsers}
                  icon={<AssessmentIcon />}
                  color="info"
                  subtitle="Prospect to merchant"
                />
              </Grid>
            )}
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
            {loading ? (
              <>
                <Skeleton variant="rectangular" width={200} height={40} />
                <Skeleton variant="rectangular" width={180} height={40} />
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/create-merchant')}
                  sx={{ bgcolor: 'primary.main' }}
                >
                  Create New Merchant
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/in-underwriting')}
                  sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                >
                  View Underwriting
                </Button>
              </>
            )}
          </Box>

          {/* Recent Activities */}
          {userRole === 'Admin' && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Recent Activities
                      </Typography>
                      {!loading && (
                        <TextField
                          placeholder="Search activities..."
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyPress={handleKeyPress}
                          sx={{ 
                            width: 300,
                            '& .MuiOutlinedInput-root': {
                              height: 40
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: searchInput && (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={handleClearSearch}
                                  edge="end"
                                >
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    </Box>
                    
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Log Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>User Name</TableCell>
                            <TableCell>Time Ago</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        {activitiesLoading ? (
                          <TableBody>
                            {[...Array(pagination.pageSize)].map((_, i) => (
                              <TableRow key={i}>
                                <TableCell colSpan={5}>
                                  <Skeleton animation="wave" height={40} />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        ) : (
                          <TableBody>
                            {dashboardData.recentActivities.list.length > 0 ? (
                              dashboardData.recentActivities.list.map((activity, index) => (
                                <TableRow key={activity.id || index}>
                                  <TableCell>{activity.log_name}</TableCell>
                                  <TableCell>{activity.description}</TableCell>
                                  <TableCell>{activity?.causer?.name || 'N/A'}</TableCell>
                                  <TableCell>{activity.time_ago}</TableCell>
                                  <TableCell>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => navigate(`/view-log/${activity?.id}`)}
                                    >
                                      <ViewIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {searchTerm ? 'No activities found for your search' : 'No recent activities'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        )}
                      </Table>
                    </TableContainer>
                    
                    {/* Pagination */}
                    {!activitiesLoading && dashboardData.recentActivities.list.length > 0 && (
                      <TablePagination
                        component="div"
                        count={pagination.total}
                        page={pagination.page - 1}
                        onPageChange={(_, newPage) => {
                          handlePageChange(newPage + 1);
                        }}
                        rowsPerPage={pagination.pageSize}
                        onRowsPerPageChange={(e) => {
                          handleRowsPerPageChange(Number(e.target.value));
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage="Rows per page"
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Quick Actions
                    </Typography>
                    {loading ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Skeleton variant="rectangular" height={40} />
                        <Skeleton variant="rectangular" height={40} />
                        <Skeleton variant="rectangular" height={40} />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => navigate('/create-merchant')}
                          startIcon={<AddIcon />}
                        >
                          Add New Merchant
                        </Button>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => navigate('/in-underwriting')}
                          startIcon={<PeopleIcon />}
                        >
                          DBA in Underwritings
                        </Button>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => navigate('/pending')}
                          startIcon={<AssessmentIcon />}
                        >
                          DBA in Pendings
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
      </Container>
    </PageLayout>
  );
};

export default Dashboard;